import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  Upload,
  Download,
  Trash2,
  Eye,
  History,
  ShieldCheck,
  FileIcon,
  HardDrive,
  Search,
  Loader2,
  FileText,
  Database,
  ArrowDownCircle,
  Link2,
  Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetStorageFilesQuery,
  useGetStorageCollectionQuery,
  useGetStorageFileVersionsQuery,
  useDeleteStorageFileMutation,
  useVerifyStorageFileMutation,
} from '@/store/api/apiSlice';
import { usePresignFileMutation } from '@/store/api/storageContentApi';
import type { StorageFile, StorageFileVersion, StorageFilePresignResult } from '@/types';
import { StorageEventType, STORAGE_UPDATES_ROOM } from '@cluster/types';
import { wsClient } from '@/lib/ws';
import { format } from 'date-fns';
import { formatBytes, downloadFile, saveBlob, planUpload, uploadSingle, uploadMultipart, computeSha256 } from '@/lib/storage-transfer';

export function StorageFilesPage() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<StorageFile | null>(null);
  const [detailFile, setDetailFile] = useState<StorageFile | null>(null);
  const [versionsFile, setVersionsFile] = useState<StorageFile | null>(null);
  const [presignFile, setPresignFile] = useState<StorageFile | null>(null);
  const [presignResult, setPresignResult] = useState<StorageFilePresignResult | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const delayedRefetchRef = useRef<number | null>(null);

  const { data: collection } = useGetStorageCollectionQuery(collectionId!);
const { data: files, isLoading, refetch } = useGetStorageFilesQuery({
    collectionId,
    search: search || undefined,
  });
  const { data: versions } = useGetStorageFileVersionsQuery(
    { fileId: versionsFile?._id ?? '' },
    { skip: !versionsFile },
  );
  const [deleteFile, { isLoading: isDeleting }] = useDeleteStorageFileMutation();
  const [verifyFile] = useVerifyStorageFileMutation();
  const [presignFileAction, { isLoading: isPresigning }] = usePresignFileMutation();

  const fileList = (files as StorageFile[] | undefined) ?? [];
  const versionList = (versions as StorageFileVersion[] | undefined) ?? [];

  // ── Real-time updates via the platform gateway ────────────────────────────
  // Storage publishes file events to Redis; the gateway fans them out to the
  // Socket.io room STORAGE_UPDATES_ROOM. The authenticated wsClient (JWT)
  // receives them and refetches the file list whenever files change.
  useEffect(() => {
    const onFilesChange = () => {
      refetch();
    };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(StorageEventType.UPLOAD_COMPLETED, onFilesChange),
      wsClient.on(StorageEventType.FILE_DELETED, onFilesChange),
      wsClient.on(StorageEventType.UPLOAD_FAILED, onFilesChange),
      wsClient.on(StorageEventType.REPLICATION_STATUS, onFilesChange),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
      wsClient.unsubscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    };
  }, [collectionId, refetch]);

  // Clear any pending delayed refetch when the page unmounts.
  useEffect(() => {
    return () => {
      if (delayedRefetchRef.current !== null) {
        clearTimeout(delayedRefetchRef.current);
        delayedRefetchRef.current = null;
      }
    };
  }, []);

  // Stats (computed inline from file list since collection stats endpoint is not available)
  const displayStats = useMemo(() => ({
    fileCount: fileList.length,
    totalSize: fileList.reduce((acc, f) => acc + f.size, 0),
    totalDownloads: Math.round(fileList.reduce((acc, f) => acc + f.downloadCount, 0) / 1000),
    replicated: fileList.filter((f) => (f.sources ?? []).length > 1).length,
  }), [fileList]);

  const columns: ColumnDef<StorageFile, unknown>[] = [
    {
      accessorKey: 'fileName',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium truncate max-w-[200px]">{row.original.fileName}</span>
        </div>
      ),
    },
    {
      accessorKey: 'size',
      header: 'Size',
      cell: ({ row }) => (
        <span className="text-sm">{formatBytes(row.original.size)}</span>
      ),
    },
    {
      accessorKey: 'mimeType',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="outline" className="text-xs">
          {row.original.mimeType ?? 'unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'sources',
      header: 'Sources',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {(row.original.sources ?? []).map((r) => (
            <Badge key={r.sourceId} variant="outline" className="text-xs gap-1">
              <HardDrive className="h-3 w-3" />
              <StatusBadge status={r.status} className="scale-75" />
            </Badge>
          ))}
        </div>
      ),
    },
    {
      accessorKey: 'downloadCount',
      header: 'Downloads',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <ArrowDownCircle className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">{(row.original.downloadCount / 1000).toFixed(row.original.downloadCount % 1000 === 0 ? 0 : 1)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {isEditableTextFile(row.original) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => { e.stopPropagation(); navigate(`/storage/files/${row.original._id}/markdown`); }}
              title="Edit markdown / text"
            >
              <FileText className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); handleDownload(row.original); }}
            title="Download"
          >
            <Download className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); setVersionsFile(row.original); }}
            title="Versions"
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); handlePresign(row.original); }}
            title="Presign"
          >
            <Link2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); handleVerify(row.original); }}
            title="Verify integrity"
          >
            <ShieldCheck className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  async function handleDownload(file: StorageFile) {
    try {
      const { blob } = await downloadFile(file._id);
      saveBlob(blob, file.fileName);
    } catch (err) {
      console.error('Download failed:', err);
      const msg =
        (err as { response?: { data?: { error?: { message?: string }; message?: string } } })
          ?.response?.data?.error?.message ??
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo descargar el archivo. Inténtalo de nuevo.';
      toastError(msg);
    }
  }

  async function handleVerify(file: StorageFile) {
    try {
      await verifyFile({ fileId: file._id }).unwrap();
      toastSuccess('Verificación iniciada. El archivo se comprobará de forma asíncrona.');
    } catch (err) {
      console.error('Verify failed:', err);
      const data = (err as { data?: { error?: { message?: string; details?: Array<{ field?: string; message?: string }> }; message?: string; details?: Array<{ field?: string; message?: string }> } })?.data;
      const details = data?.error?.details ?? data?.details;
      let msg =
        data?.error?.message ??
        data?.message ??
        'No se pudo verificar la integridad del archivo. Inténtalo de nuevo.';
      if (Array.isArray(details) && details.length > 0) {
        msg = details.map((d) => d.message ?? d.field).filter(Boolean).join(', ');
      }
      toastError(msg);
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      await deleteFile({ fileId: deleteTarget._id }).unwrap();
      setDeleteTarget(null);
    }
  }

  /**
   * Generate a short-lived share link (AC-12). The dialog opens immediately with
   * a spinner while the one-time link is minted, then reveals the URL.
   */
  async function handlePresign(file: StorageFile) {
    setPresignFile(file);
    setPresignResult(null);
    try {
      const result = await presignFileAction({ fileId: file._id, ttlSeconds: 900 }).unwrap();
      setPresignResult(result);
    } catch (err) {
      console.error('Presign failed:', err);
      const data = (err as { data?: { error?: { message?: string }; message?: string } })?.data;
      toastError(
        data?.error?.message ??
          data?.message ??
          'No se pudo generar el enlace para compartir. Inténtalo de nuevo.',
      );
    }
  }

  async function handleCopyLink() {
    if (!presignResult) return;
    try {
      await navigator.clipboard.writeText(presignResult.url);
      toastSuccess('Link copied');
    } catch (err) {
      console.error('Copy failed:', err);
      toastError('No se pudo copiar el enlace.');
    }
  }

  async function handleUpload() {
    const fileInput = fileInputRef.current;
    if (!fileInput?.files?.[0] || !collectionId) return;

    if (delayedRefetchRef.current !== null) {
      clearTimeout(delayedRefetchRef.current);
      delayedRefetchRef.current = null;
    }

    const file = fileInput.files[0];
    setUploadProgress(0);
    setUploadError(null);

    try {
      const plan = planUpload(file);

      if (plan.mode === 'single') {
        await uploadSingle(file, collectionId, (pct) => setUploadProgress(pct), uploadDescription);
      } else {
        const sha256 = await computeSha256(file);
        await uploadMultipart(file, collectionId, sha256, 10, (pct) => setUploadProgress(pct));
      }

      setUploadProgress(100);
      // Refetch is deferred to cover the async file-row write race on the
      // backend: an immediate refetch can run before the document is persisted.
      delayedRefetchRef.current = window.setTimeout(() => {
        delayedRefetchRef.current = null;
        refetch().catch(() => {});
      }, 1500);
      setTimeout(() => {
        setUploadOpen(false);
        setUploadProgress(null);
        if (fileInput) fileInput.value = '';
      }, 1000);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setUploadProgress(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/storage/collections')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            Files — {collection?.name ?? 'Loading...'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Browse and manage files in this collection
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload
        </Button>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Files" value={displayStats.fileCount} icon={<FileIcon className="h-4 w-4 text-muted-foreground" />} />
        <StatCard title="Total Size" value={displayStats.totalSize} icon={<Database className="h-4 w-4 text-muted-foreground" />} formatValue />
        <StatCard title="Downloads" value={displayStats.totalDownloads} icon={<Download className="h-4 w-4 text-muted-foreground" />} />
        <StatCard title="Replicated" value={displayStats.replicated} icon={<HardDrive className="h-4 w-4 text-muted-foreground" />} />
      </div>

      {/* Search + table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search files..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : fileList.length === 0 ? (
            <EmptyState
              title="No files"
              description="Upload files to this collection"
              action={<Button onClick={() => setUploadOpen(true)}>Upload File</Button>}
            />
          ) : (
            <DataTable columns={columns} data={fileList} />
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(open) => { if (!open) { setUploadProgress(null); setUploadError(null); setUploadDescription(''); } setUploadOpen(open); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
            <DialogDescription>
              Select a file to upload. Files over 50 MB will use multipart upload.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              ref={fileInputRef}
              type="file"
              onChange={() => setUploadError(null)}
            />
            <div className="space-y-2">
              <Label htmlFor="uploadDescription">Description (optional)</Label>
              <textarea
                id="uploadDescription"
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="What is this file about?"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
              />
            </div>
            {uploadProgress !== null && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            {uploadError && (
              <p className="text-sm text-destructive">{uploadError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploadProgress !== null}>
              {uploadProgress !== null ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File detail dialog */}
      <Dialog open={!!detailFile} onOpenChange={(open) => !open && setDetailFile(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              File Details
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {detailFile?.fileName}
            </DialogDescription>
          </DialogHeader>
          {detailFile && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Size:</span> {formatBytes(detailFile.size)}</div>
                <div><span className="text-muted-foreground">Type:</span> {detailFile.mimeType ?? 'unknown'}</div>
                <div><span className="text-muted-foreground">Status:</span> <StatusBadge status={detailFile.status} /></div>
                <div><span className="text-muted-foreground">Downloads:</span> {(detailFile.downloadCount / 1000).toFixed(detailFile.downloadCount % 1000 === 0 ? 0 : 1)}</div>
                {detailFile.sha256 && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">SHA-256:</span>{' '}
                    <code className="text-xs">{detailFile.sha256}</code>
                  </div>
                )}
                <div><span className="text-muted-foreground">Created:</span> {format(new Date(detailFile.createdAt), 'MMM d, yyyy HH:mm')}</div>
                <div><span className="text-muted-foreground">Updated:</span> {format(new Date(detailFile.updatedAt), 'MMM d, yyyy HH:mm')}</div>
              </div>

              {/* Replica status */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Sources</h3>
                {(detailFile.sources ?? []).length > 0 ? (
                  <div className="rounded-md border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-3 py-2 text-left font-medium">Source</th>
                          <th className="px-3 py-2 text-left font-medium">Status</th>
                          <th className="px-3 py-2 text-left font-medium">Verified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailFile.sources ?? []).map((r) => (
                          <tr key={r.sourceId} className="border-b last:border-0">
                            <td className="px-3 py-2 font-mono text-xs">{r.sourceId}</td>
                            <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {r.lastVerifiedAt ? format(new Date(r.lastVerifiedAt), 'MMM d, HH:mm') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No sources</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={!!versionsFile} onOpenChange={(open) => !open && setVersionsFile(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              File Versions
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {versionsFile?.fileName}
            </DialogDescription>
          </DialogHeader>
          {versionList.length > 0 ? (
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-3 py-2 text-left font-medium">Version</th>
                    <th className="px-3 py-2 text-left font-medium">Size</th>
                    <th className="px-3 py-2 text-left font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {versionList.map((v) => (
                    <tr key={v._id} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">v{v.versionNumber}</td>
                      <td className="px-3 py-2">{formatBytes(v.size)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {format(new Date(v.createdAt), 'MMM d, yyyy HH:mm')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">No version history</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Presign (one-time share link) dialog */}
      <Dialog
        open={!!presignFile}
        onOpenChange={(open) => {
          if (!open) {
            setPresignFile(null);
            setPresignResult(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Share Link
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {presignFile?.fileName}
            </DialogDescription>
          </DialogHeader>
          {isPresigning ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Generating link...
            </div>
          ) : presignResult ? (
            <div className="space-y-3">
              <Input
                readOnly
                value={presignResult.url}
                onFocus={(e) => e.currentTarget.select()}
                className="select-all font-mono text-xs"
              />
              <Button variant="outline" size="sm" onClick={handleCopyLink}>
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </Button>
              <p className="text-xs text-muted-foreground">
                One-time link — expires at{' '}
                {format(new Date(presignResult.expiresAt), 'MMM d, yyyy HH:mm')}. Works once.
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete File"
        description={
          collection?.settings.softDeleteDays && collection.settings.softDeleteDays > 0
            ? `"${deleteTarget?.fileName}" will be soft-deleted and permanently purged after ${collection.settings.softDeleteDays} days.`
            : `Are you sure you want to permanently delete "${deleteTarget?.fileName}"? This action cannot be undone.`
        }
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────

/** Files whose content can be edited with the markdown editor (ST-3/ST-4). */
export function isEditableTextFile(file: StorageFile): boolean {
  if (!file) return false;
  const name = (file.fileName ?? '').toLowerCase();
  const ext = (file.extension ?? '').toLowerCase() || (name.split('.').pop() ?? '');
  const mime = (file.mimeType ?? '').toLowerCase();
  return (
    ext === 'md' ||
    mime === 'text/markdown' ||
    mime.startsWith('text/') ||
    name.endsWith('.md')
  );
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  formatValue?: boolean;
}

function StatCard({ title, value, icon, formatValue }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {formatValue ? formatBytes(value) : value}
        </div>
      </CardContent>
    </Card>
  );
}
